import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Slider } from '../components/ui/slider';
import { Users, Coins, CreditCard, Shield, Globe, Zap, Sparkles, Plus, Trash2, GripVertical, Search as SearchIcon, Crown, Image as ImageIcon, MessageSquare, Video } from 'lucide-react';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [cashouts, setCashouts] = useState([]);

  const load = async () => {
    const [s, st, u, c] = await Promise.all([
      api.get('/admin/stats'), api.get('/admin/settings'), api.get('/admin/users'), api.get('/admin/cashouts')
    ]);
    setStats(s.data); setSettings(st.data); setUsers(u.data); setCashouts(c.data);
  };
  useEffect(() => { load(); }, []);

  const update = async (patch) => {
    try {
      const { data } = await api.put('/admin/settings', { data: patch });
      setSettings(data); toast.success('Saved');
    } catch (e) { toast.error('Failed'); }
  };

  const cashAction = async (id, status) => {
    await api.post(`/admin/cashouts/${id}/action`, { status });
    toast.success(`Marked ${status}`); load();
  };
  const userAction = async (uid, action, extra = {}) => {
    await api.post(`/admin/users/${uid}/action`, { action, ...extra });
    toast.success('Done'); load();
  };

  if (!settings) return <div className="p-10 text-center text-slate-400">Loading admin…</div>;

  const F = ({ k, label, type = 'number' }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={`set-${k}`} type={type} defaultValue={settings[k]} onBlur={e => {
        const v = type === 'number' ? +e.target.value : e.target.value;
        if (v !== settings[k]) update({ [k]: v });
      }} />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6 py-6 px-4">
      <div className="flex items-center gap-2"><Shield className="w-7 h-7 text-violet-400" /><h1 className="font-display text-3xl font-bold">Admin Dashboard</h1></div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['Users', stats?.total_users, Users],
          ['Premium', stats?.premium_users, Zap],
          ['Cashouts pending', stats?.pending_cashouts, CreditCard],
          ['JXP issued', stats?.jxp_issued, Coins],
        ].map(([l, v, I]) => (
          <Card key={l} className="glass p-4">
            <I className="w-4 h-4 text-violet-400 mb-2" />
            <div className="text-xs text-slate-400">{l}</div>
            <div className="font-display text-2xl font-black">{v || 0}</div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="economy">
        <TabsList className="glass flex flex-wrap h-auto">
          <TabsTrigger value="economy" data-testid="tab-economy">Economy</TabsTrigger>
          <TabsTrigger value="subs" data-testid="tab-subs">Subscriptions</TabsTrigger>
          <TabsTrigger value="credits" data-testid="tab-credits">Credits</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="member-subs" data-testid="tab-member-subs">Member Subs</TabsTrigger>
          <TabsTrigger value="prompts" data-testid="tab-prompts">Trending Prompts</TabsTrigger>
          <TabsTrigger value="cashouts" data-testid="tab-cashouts">Cashouts</TabsTrigger>
          <TabsTrigger value="features" data-testid="tab-features">Features</TabsTrigger>
          <TabsTrigger value="domain" data-testid="tab-domain">Domain & PWA</TabsTrigger>
          <TabsTrigger value="recording" data-testid="tab-recording">Recording</TabsTrigger>
          <TabsTrigger value="coins" data-testid="tab-coins">Coins</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="tab-broadcast">Broadcast</TabsTrigger>
        </TabsList>

        <TabsContent value="economy" className="space-y-3">
          <AdminBalances />
          <Card className="glass p-5">
            <div className="font-display font-bold mb-3">JXP Rewards</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <F k="reward_daily_login" label="Daily login" />
              <F k="reward_streak_7d" label="7-day streak" />
              <F k="reward_referral" label="Referral" />
              <F k="reward_create_content" label="Create content" />
              <F k="reward_chat_message" label="Chat msg" />
              <F k="reward_image_generate" label="Image gen" />
              <F k="reward_video_generate" label="Video gen" />
            </div>
          </Card>
          <Card className="glass p-5">
            <div className="font-display font-bold mb-3">Cash-out & Rates</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <F k="jxp_to_usd_rate" label="JXP per $1" />
              <F k="min_cashout_usd" label="Min cash-out ($)" />
              <F k="cashout_fee_usd" label="Fee ($)" />
              <F k="max_daily_rewards" label="Max daily JXP" />
              <F k="max_referral_rewards" label="Max referral JXP" />
            </div>
          </Card>
          <Card className="glass p-5">
            <div className="font-display font-bold mb-3">Boost Prices (JXP)</div>
            <div className="grid grid-cols-3 gap-3">
              <F k="boost_1h_price_jxp" label="1 Hour" />
              <F k="boost_6h_price_jxp" label="6 Hours" />
              <F k="boost_24h_price_jxp" label="24 Hours" />
            </div>
          </Card>
          <AdminBudgetAlerts settings={settings} update={update} />
        </TabsContent>

        <TabsContent value="subs" className="space-y-3">
          <Card className="glass p-5">
            <div className="font-display font-bold mb-3">Subscription Prices</div>
            <div className="grid grid-cols-2 gap-3">
              <F k="sub_premium_price" label="Premium ($/mo)" />
              <F k="sub_elite_price" label="Elite ($/mo)" />
            </div>
          </Card>
          <Card className="glass p-5">
            <div className="font-display font-bold mb-2">Membership Benefits (public feature list)</div>
            <div className="text-[11px] text-slate-500 mb-3">Shown on the Subscriptions page. One benefit per line.</div>
            <AdminSubFeatures k="sub_premium_features" label="Premium" settings={settings} update={update} />
            <AdminSubFeatures k="sub_elite_features" label="Elite" settings={settings} update={update} />
          </Card>
          <Card className="glass p-5">
            <div className="font-display font-bold mb-3">AI Limits</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <F k="free_daily_chat_limit" label="Free chat/day" />
              <F k="free_daily_image_limit" label="Free image/day" />
              <F k="free_daily_video_limit" label="Free video/day" />
              <F k="premium_daily_chat_limit" label="Premium chat/day" />
              <F k="premium_daily_image_limit" label="Premium image/day" />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="credits">
          <AdminCredits users={users} />
        </TabsContent>

        <TabsContent value="users">
          <Card className="glass p-2">
            {users.map(u => (
              <div key={u.id} className="flex items-center gap-2 p-3 border-b border-white/5 text-sm" data-testid={`admin-user-${u.id}`}>
                <div className="flex-1">
                  <div className="font-semibold">{u.display_name} <span className="text-slate-400 text-xs">@{u.username}</span></div>
                  <div className="text-xs text-slate-400">
                    {u.email} • JXP: {u.jxp} • USD: ${(u.wallet_usd_balance || 0).toFixed(2)} • Credits: {u.credits_balance || 0} • Streak: {u.streak}
                  </div>
                </div>
                {u.is_admin && <span className="text-xs px-2 py-0.5 rounded bg-violet-500/20 text-violet-300">Admin</span>}
                {u.suspended && <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-300">Suspended</span>}
                <Button data-testid={`admin-user-view-${u.id}`} size="sm" variant="outline" onClick={() => window.location.assign(`/admin/users/${u.id}`)} className="border-violet-500/40 text-violet-200">View</Button>
                <Button size="sm" variant="outline" onClick={() => userAction(u.id, u.suspended ? 'unsuspend' : 'suspend')}>{u.suspended ? 'Unsuspend' : 'Suspend'}</Button>
                <Button data-testid={`admin-grant-jxp-${u.id}`} size="sm" variant="outline" onClick={() => { const a = prompt('Grant JXP (coins):'); if (a) userAction(u.id, 'grant_jxp', { amount: +a }); }}>+JXP</Button>
                <Button data-testid={`admin-grant-usd-${u.id}`} size="sm" variant="outline" className="border-emerald-500/40 text-emerald-200" onClick={() => { const a = prompt('Give money (USD, negative to deduct):'); if (a) userAction(u.id, 'grant_usd', { amount: +a }); }}>+$</Button>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="prompts">
          <AdminTrendingPrompts />
        </TabsContent>

        <TabsContent value="member-subs">
          <AdminMemberSubs users={users} refresh={load} />
        </TabsContent>

        <TabsContent value="cashouts">
          <Card className="glass p-2">
            {cashouts.length === 0 && <div className="p-6 text-slate-400 text-sm text-center">No cashouts yet</div>}
            {cashouts.map(c => (
              <div key={c.id} className="p-3 border-b border-white/5 text-sm" data-testid={`admin-cash-${c.id}`}>
                <div className="flex justify-between">
                  <div><span className="font-bold">${c.usd_amount}</span> via {c.method}</div>
                  <span className="text-xs uppercase tracking-wider">{c.status}</span>
                </div>
                <div className="text-xs text-slate-400 mt-1">{c.details}</div>
                <div className="flex gap-2 mt-2">
                  {['under_review','approved','paid','rejected','fraud_review'].map(s => (
                    <Button key={s} size="sm" variant="outline" onClick={() => cashAction(c.id, s)}>{s}</Button>
                  ))}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="features">
          <Card className="glass p-5 space-y-4">
            {['feature_marketplace','feature_leaderboards','feature_spin_wheel','feature_video_gen'].map(k => (
              <div key={k} className="flex items-center justify-between">
                <Label className="capitalize">{k.replace('feature_','').replace(/_/g,' ')}</Label>
                <Switch data-testid={`switch-${k}`} checked={settings[k]} onCheckedChange={v => update({ [k]: v })} />
              </div>
            ))}
          </Card>

          {/* Tier Editor — live-tune free/premium daily caps without a redeploy */}
          <Card className="glass p-5 space-y-5 mt-3" data-testid="tier-editor">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-300" />
              <div className="font-display font-bold">Tier Editor — Daily Caps</div>
            </div>
            <div className="text-[11px] text-slate-500 -mt-2">
              Drag a slider to adjust the daily limit for each tier. Saves instantly.
            </div>
            <TierSlider k="free_daily_chat_limit"    icon={<MessageSquare className='w-3.5 h-3.5' />} label="Free · Chat / day"         min={5}  max={200} step={5}  settings={settings} update={update} />
            <TierSlider k="free_daily_image_limit"   icon={<ImageIcon     className='w-3.5 h-3.5' />} label="Free · Images / day"       min={0}  max={100} step={1}  settings={settings} update={update} />
            <TierSlider k="free_daily_video_limit"   icon={<Video         className='w-3.5 h-3.5' />} label="Free · Videos / day"       min={0}  max={20}  step={1}  settings={settings} update={update} />
            <TierSlider k="premium_daily_chat_limit" icon={<MessageSquare className='w-3.5 h-3.5' />} label="Premium · Chat / day"      min={50} max={1000} step={50} settings={settings} update={update} />
            <TierSlider k="premium_daily_image_limit" icon={<ImageIcon    className='w-3.5 h-3.5' />} label="Premium · Images / day"    min={10} max={1000} step={10} settings={settings} update={update} />
          </Card>

          {/* Watermark size — admins drag these to grow/shrink the "Jixxeo AI" mark */}
          <Card className="glass p-5 space-y-5 mt-3" data-testid="watermark-editor">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-violet-300" />
              <div className="font-display font-bold">Download Watermark Size</div>
            </div>
            <div className="text-[11px] text-slate-500 -mt-2">
              Images use <strong>% of the shorter side</strong> for text height (bigger % = bigger mark).
              Videos use a divisor (<strong>smaller = bigger text</strong>). Changes apply to the NEXT download — pre-cached files aren't retroactive.
            </div>
            <TierSlider k="download_watermark_scale_image"
                        icon={<ImageIcon className='w-3.5 h-3.5' />}
                        label="Image · text size %"
                        min={1.5} max={12} step={0.5}
                        settings={settings} update={update} />
            <TierSlider k="download_watermark_scale_video"
                        icon={<Video className='w-3.5 h-3.5' />}
                        label="Video · height ÷ (smaller = BIGGER)"
                        min={10} max={60} step={1}
                        settings={settings} update={update} />
            <TierSlider k="download_watermark_opacity"
                        icon={<Sparkles className='w-3.5 h-3.5' />}
                        label="Opacity"
                        min={0.3} max={1} step={0.05}
                        settings={settings} update={update} />
          </Card>
        </TabsContent>

        <TabsContent value="domain">
          <Card className="glass p-5 space-y-3">
            <div className="flex items-center gap-2"><Globe className="w-5 h-5 text-cyan-400" /><div className="font-display font-bold">Domain & PWA</div></div>
            <F k="primary_domain" label="Primary domain" type="text" />
            <F k="custom_domain" label="Custom domain" type="text" />
            <F k="pwa_name" label="PWA Name" type="text" />
            <F k="pwa_short_name" label="PWA Short Name" type="text" />
            <F k="pwa_description" label="Description" type="text" />
            <F k="pwa_theme_color" label="Theme color (hex)" type="text" />
            <F k="pwa_start_url" label="Start URL" type="text" />
            <div className="text-xs text-slate-400 mt-3">
              To connect your custom domain: point an A record or CNAME at the platform host, then set it above. Once DNS propagates, users can install Jixxeo AI as a PWA from your domain.
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="recording">
          <Card className="glass p-5 space-y-4">
            <div className="font-display font-bold text-lg">Recording Settings</div>
            <div className="flex items-center justify-between">
              <Label>Enable recording feature</Label>
              <Switch data-testid="switch-recording_enabled" checked={settings.recording_enabled} onCheckedChange={v => update({ recording_enabled: v })} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <F k="recording_max_duration_sec" label="Max duration (seconds)" />
              <F k="recording_max_size_mb" label="Max file size (MB)" />
              <F k="recording_max_per_member" label="Max recordings / member" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Default quality</Label>
                <select
                  data-testid="set-recording_default_quality"
                  defaultValue={settings.recording_default_quality}
                  onBlur={e => e.target.value !== settings.recording_default_quality && update({ recording_default_quality: e.target.value })}
                  className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm"
                >
                  <option value="standard">Standard (720p)</option>
                  <option value="hd">HD (1080p)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Watermark position</Label>
                <select
                  data-testid="set-recording_watermark_position"
                  defaultValue={settings.recording_watermark_position}
                  onBlur={e => e.target.value !== settings.recording_watermark_position && update({ recording_watermark_position: e.target.value })}
                  className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm"
                >
                  <option value="bottom-right">Bottom right</option>
                  <option value="bottom-left">Bottom left</option>
                  <option value="top-right">Top right</option>
                  <option value="top-left">Top left</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F k="recording_watermark_text" label="Watermark text" type="text" />
              <F k="recording_watermark_opacity" label="Watermark opacity (0-1)" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Allow downloads</Label>
              <Switch data-testid="switch-recording_allow_downloads" checked={settings.recording_allow_downloads} onCheckedChange={v => update({ recording_allow_downloads: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Allow sharing</Label>
              <Switch data-testid="switch-recording_allow_sharing" checked={settings.recording_allow_sharing} onCheckedChange={v => update({ recording_allow_sharing: v })} />
            </div>
            <p className="text-[11px] text-slate-500">Recordings are stored locally on each member's device. The "Jixxeo AI" watermark is baked into every export and cannot be removed by normal members.</p>
          </Card>
        </TabsContent>

        <TabsContent value="broadcast">
          <AdminBroadcast />
        </TabsContent>

        <TabsContent value="coins" className="space-y-3">
          <AdminCoins settings={settings} update={update} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Admin: Provider Balances (fal.ai / Stripe / Universal Key) ============
function AdminBalances() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    const load = async () => {
      try { const { data } = await api.get('/admin/provider-balances'); setData(data); }
      catch { /* noop */ }
    };
    load();
    const t = setInterval(load, 60_000); // auto-refresh each minute
    return () => clearInterval(t);
  }, []);
  if (!data) return null;
  const falExhausted = data.fal?.status === 'exhausted';
  const row = (name, info) => {
    const status = info?.status || 'unknown';
    const color = status === 'exhausted' ? 'text-rose-200 bg-rose-500/10 border-rose-500/40'
                : status === 'ok' ? 'text-emerald-200 bg-emerald-500/10 border-emerald-500/30'
                : status === 'not_configured' ? 'text-slate-400 bg-white/5 border-white/10'
                : 'text-amber-200 bg-amber-500/10 border-amber-500/30';
    return (
      <div data-testid={`balance-${name.toLowerCase()}`} className={`rounded-xl border p-3 ${color}`}>
        <div className="flex items-center justify-between">
          <div className="font-bold">{name}</div>
          <div className="text-[10px] uppercase tracking-wider font-bold">{status.replace('_', ' ')}</div>
        </div>
        <div className="text-xs mt-1 opacity-90">{info?.message || '—'}</div>
        {info?.gross_7d_usd != null && (
          <div className="text-[10px] mt-1 opacity-70">7-day gross: ${info.gross_7d_usd.toFixed(2)}</div>
        )}
      </div>
    );
  };
  return (
    <div className="space-y-3" data-testid="admin-balances">
      {falExhausted && (
        <div data-testid="admin-fal-exhausted-banner"
             className="rounded-xl bg-rose-500/20 border-2 border-rose-500 p-3 text-rose-100 text-sm font-bold flex items-start gap-2 animate-pulse">
          <span className="text-xl leading-none">⚠️</span>
          <div>
            <div>fal.ai balance EXHAUSTED — Jixxeo Cinema is refunding every video right now.</div>
            <a href="https://fal.ai/dashboard/billing" target="_blank" rel="noreferrer"
               className="underline text-rose-50 font-black">Top up at fal.ai/dashboard/billing →</a>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {row('fal.ai', data.fal)}
        {row('Stripe', data.stripe)}
        {row('Universal Key', data.emergent_llm)}
      </div>
    </div>
  );
}

// ============ Admin: Budget alerts (email the moment Universal Key or fal.ai goes dry) ============
function AdminBudgetAlerts({ settings, update }) {
  const [email, setEmail] = React.useState(settings.budget_alert_email || '');
  const [cooldown, setCooldown] = React.useState(settings.budget_alert_cooldown_min ?? 60);
  const [enabled, setEnabled] = React.useState(settings.budget_alert_enabled ?? true);
  const [testing, setTesting] = React.useState(false);
  const dirty = email !== (settings.budget_alert_email || '')
             || cooldown !== (settings.budget_alert_cooldown_min ?? 60)
             || enabled !== (settings.budget_alert_enabled ?? true);
  const save = () => update({
    budget_alert_email: (email || '').trim(),
    budget_alert_cooldown_min: Math.max(1, Math.min(1440, +cooldown || 60)),
    budget_alert_enabled: !!enabled,
  });
  const sendTest = async () => {
    try {
      setTesting(true);
      await api.post('/admin/budget-alert/test');
      toast.success(`Test alert sent to ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send test email');
    } finally {
      setTesting(false);
    }
  };
  return (
    <Card className="glass p-5 space-y-3" data-testid="admin-budget-alerts">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold">Low-Budget Email Alerts</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            We'll email you the moment the Universal Key or fal.ai balance runs dry — before members see a "service limit" error.
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-300">
          <Switch data-testid="budget-alerts-toggle" checked={enabled} onCheckedChange={setEnabled} />
          <span>{enabled ? 'On' : 'Off'}</span>
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3">
        <div>
          <Label className="text-xs">Alert inbox</Label>
          <Input data-testid="budget-alert-email" type="email" value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@yourdomain.com" />
        </div>
        <div>
          <Label className="text-xs">Cooldown (minutes)</Label>
          <Input data-testid="budget-alert-cooldown" type="number" min={1} max={1440}
            value={cooldown} onChange={e => setCooldown(e.target.value)} />
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end pt-1">
        <Button data-testid="budget-alert-test" size="sm" variant="outline"
          disabled={!email || testing}
          onClick={sendTest}>
          {testing ? 'Sending…' : 'Send test email'}
        </Button>
        <Button data-testid="budget-alert-save" size="sm" className="gradient-brand text-white"
          disabled={!dirty} onClick={save}>Save</Button>
      </div>
    </Card>
  );
}



// ============ Admin: Coins (packages + shop pricing) ============
function AdminCoins({ settings, update }) {
  const [packages, setPackages] = React.useState(settings.coin_packages || []);
  const [prices, setPrices] = React.useState(settings.coinshop_coin_prices || {});

  React.useEffect(() => {
    setPackages(settings.coin_packages || []);
    setPrices(settings.coinshop_coin_prices || {});
  }, [settings.coin_packages, settings.coinshop_coin_prices]);

  const savePackages = () => update({ coin_packages: packages });
  const savePrices = () => update({ coinshop_coin_prices: prices });

  const updatePkg = (i, patch) => setPackages(prev => prev.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const removePkg = (i) => setPackages(prev => prev.filter((_, idx) => idx !== i));
  const addPkg = () => setPackages(prev => [...prev, { id: `coins_${Date.now()}`, title: 'New Pack', coins: 100, price_usd: 0.99, popular: false }]);

  return (
    <div className="space-y-3" data-testid="admin-coins">
      <Card className="glass p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold">Coin packages (real-money purchases)</div>
          <div className="flex gap-2">
            <Button data-testid="coin-pack-add" size="sm" variant="outline" onClick={addPkg}>Add pack</Button>
            <Button data-testid="coin-pack-save" size="sm" className="gradient-brand text-white" onClick={savePackages}>Save packages</Button>
          </div>
        </div>
        <div className="space-y-2">
          {packages.map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`coin-pack-row-${i}`}>
              <Input className="col-span-3" value={p.title} onChange={e => updatePkg(i, { title: e.target.value })} placeholder="Title" />
              <Input className="col-span-3" value={p.id} onChange={e => updatePkg(i, { id: e.target.value })} placeholder="id (unique)" />
              <Input className="col-span-2" type="number" value={p.coins} onChange={e => updatePkg(i, { coins: +e.target.value })} placeholder="Coins" />
              <Input className="col-span-2" type="number" step="0.01" value={p.price_usd} onChange={e => updatePkg(i, { price_usd: +e.target.value })} placeholder="USD" />
              <label className="col-span-1 flex items-center gap-1 text-xs">
                <input type="checkbox" checked={!!p.popular} onChange={e => updatePkg(i, { popular: e.target.checked })} /> Pop
              </label>
              <Button size="sm" variant="outline" className="col-span-1" onClick={() => removePkg(i)}>✕</Button>
            </div>
          ))}
          {packages.length === 0 && <div className="text-xs text-slate-500">No packages yet.</div>}
        </div>
      </Card>

      <Card className="glass p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold">Coin Shop item prices (in Coins)</div>
          <Button data-testid="coin-price-save" size="sm" className="gradient-brand text-white" onClick={savePrices}>Save prices</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {['streak_freeze', 'boost_1h', 'boost_24h', 'extra_spin', 'premium_trial', 'chat_boost', 'badge_gold'].map(sku => (
            <div key={sku} className="flex items-center justify-between gap-2">
              <Label className="text-xs capitalize flex-1">{sku.replace(/_/g, ' ')}</Label>
              <Input
                data-testid={`coin-price-${sku}`}
                type="number"
                className="w-28"
                value={prices[sku] ?? ''}
                onChange={e => setPrices(prev => ({ ...prev, [sku]: +e.target.value }))}
                placeholder="Coins"
              />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ============ Admin: Broadcast Notifications ============
function AdminBroadcast() {
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [ctaUrl, setCtaUrl] = React.useState('/');
  const [type, setType] = React.useState('ADMIN_NOTIFICATION');
  const [audience, setAudience] = React.useState('all');
  const [sending, setSending] = React.useState(false);
  const [stats, setStats] = React.useState(null);
  const [lastResult, setLastResult] = React.useState(null);

  const loadStats = async () => {
    try { const { data } = await api.get('/admin/notifications/stats'); setStats(data); }
    catch { /* noop */ }
  };
  React.useEffect(() => { loadStats(); }, []);

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error('Title and body are required'); return; }
    if (!window.confirm(`Send to all users in audience "${audience}"? This cannot be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/admin/notifications/broadcast', {
        title: title.trim(), body: body.trim(), cta_url: ctaUrl || '/', type, audience,
      });
      setLastResult(data);
      toast.success(`Sent to ${data.sent_to} members`);
      setTitle(''); setBody('');
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  const sendTest = async () => {
    try {
      await api.post('/push/test', {});
      toast.success('Test push sent to your own devices');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed — enable notifications first');
    }
  };

  return (
    <div className="space-y-3">
      <Card className="glass p-5 space-y-3" data-testid="admin-broadcast-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">Broadcast notification</div>
            <p className="text-[11px] text-slate-500">Delivers an in-app notification + native Web Push to subscribed devices.</p>
          </div>
          {stats && (
            <div className="text-right text-[11px] text-slate-400">
              <div><span className="text-violet-300 font-bold">{stats.users_with_push}</span> members with push</div>
              <div><span className="text-violet-300 font-bold">{stats.push_subscriptions}</span> active devices</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input data-testid="broadcast-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="New: Seedance 2.5 is here" maxLength={80} />
          </div>
          <div>
            <Label className="text-xs">Deep link (CTA)</Label>
            <Input data-testid="broadcast-cta" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="/ai/video" />
          </div>
        </div>

        <div>
          <Label className="text-xs">Message</Label>
          <Textarea data-testid="broadcast-body" value={body} onChange={e => setBody(e.target.value)} rows={3} maxLength={240} placeholder="Make a cinematic 10-second clip with audio in one tap." />
          <div className="text-[10px] text-slate-500 text-right">{body.length}/240</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Audience</Label>
            <select
              data-testid="broadcast-audience"
              value={audience}
              onChange={e => setAudience(e.target.value)}
              className="w-full h-10 rounded-md bg-slate-900/60 border border-white/10 px-3 text-sm"
            >
              <option value="all">All members</option>
              <option value="free">Free tier only</option>
              <option value="premium">Premium only</option>
              <option value="elite">Elite only</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <select
              data-testid="broadcast-type"
              value={type}
              onChange={e => setType(e.target.value)}
              className="w-full h-10 rounded-md bg-slate-900/60 border border-white/10 px-3 text-sm"
            >
              <option value="ADMIN_NOTIFICATION">General announcement</option>
              <option value="NEW_FEATURE">New feature</option>
              <option value="SYSTEM_ANNOUNCEMENT">System announcement</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button data-testid="broadcast-send" onClick={send} disabled={sending} className="gradient-brand text-white font-bold">
            {sending ? 'Sending…' : 'Send broadcast'}
          </Button>
          <Button data-testid="broadcast-test" variant="outline" onClick={sendTest} disabled={sending}>
            Send test to me
          </Button>
          {lastResult && (
            <div className="text-xs text-emerald-300" data-testid="broadcast-last-result">
              ✓ Delivered to {lastResult.sent_to} members
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ============ Admin: Trending Prompts ============
function AdminTrendingPrompts() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // null | 'new' | prompt_id
  const [form, setForm] = React.useState({ text: '', category: 'Chat', icon: 'spark', route: '/ai/chat', description: '', status: 'active', featured: false, display_order: 0 });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/trending-prompts'); setItems(data); } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm({ text: '', category: 'Chat', icon: 'spark', route: '/ai/chat', description: '', status: 'active', featured: false, display_order: items.length });
    setEditing('new');
  };
  const startEdit = (p) => { setForm({ ...p }); setEditing(p.id); };
  const save = async () => {
    if (!form.text?.trim()) { toast.error('Prompt text is required'); return; }
    try {
      if (editing === 'new') await api.post('/admin/trending-prompts', form);
      else await api.put(`/admin/trending-prompts/${editing}`, form);
      toast.success('Saved'); setEditing(null); load();
    } catch { toast.error('Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this prompt?')) return;
    try { await api.delete(`/admin/trending-prompts/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };
  const toggleStatus = async (p) => {
    const next = p.status === 'active' ? 'inactive' : 'active';
    await api.put(`/admin/trending-prompts/${p.id}`, { ...p, status: next });
    load();
  };
  const move = async (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await api.post('/admin/trending-prompts/reorder', { ids: next.map(x => x.id) });
  };

  return (
    <Card className="glass p-5 space-y-4" data-testid="admin-prompts-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-violet-400" />
          <div className="font-display font-bold">Trending Prompts</div>
          <span className="text-xs text-slate-500">({items.length})</span>
        </div>
        <Button data-testid="admin-prompt-new" size="sm" onClick={startNew} className="gradient-brand text-white"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>

      {editing && (
        <Card className="p-4 bg-violet-500/5 border-violet-500/30 space-y-2" data-testid="admin-prompt-form">
          <Label className="text-xs">Prompt text</Label>
          <Input data-testid="admin-prompt-text" value={form.text} onChange={e => setForm({ ...form, text: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label className="text-xs">Icon</Label><Input value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} placeholder="spark/image/code/pen/globe/book/video" /></div>
          </div>
          <div><Label className="text-xs">Route (on tap)</Label><Input value={form.route} onChange={e => setForm({ ...form, route: e.target.value })} /></div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.status === 'active'} onCheckedChange={v => setForm({ ...form, status: v ? 'active' : 'inactive' })} /> Active</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.featured} onCheckedChange={v => setForm({ ...form, featured: v })} /> Featured</label>
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="admin-prompt-save" onClick={save} className="gradient-brand text-white">Save</Button>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Preview strip */}
      <div className="rounded-xl bg-slate-950/50 border border-white/10 p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Homepage preview</div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {items.filter(x => x.status === 'active').map((p, i) => (
            <div key={i} className="flex-shrink-0 px-3 py-1.5 rounded-full bg-white/5 border border-violet-500/25 text-xs">{p.text}</div>
          ))}
          {items.filter(x => x.status === 'active').length === 0 && <div className="text-xs text-slate-500">No active prompts</div>}
        </div>
      </div>

      {loading ? <div className="text-center text-slate-400 py-6 text-sm">Loading…</div> : (
        <div className="space-y-1">
          {items.map((p, i) => (
            <div key={p.id} data-testid={`admin-prompt-${p.id}`} className={`flex items-center gap-2 p-3 rounded-lg border text-sm
              ${p.status === 'active' ? 'bg-white/5 border-white/10' : 'bg-slate-900/50 border-white/5 opacity-60'}`}>
              <div className="flex flex-col">
                <button data-testid={`admin-prompt-up-${p.id}`} onClick={() => move(i, -1)} className="text-slate-400 hover:text-white text-xs">▲</button>
                <button data-testid={`admin-prompt-down-${p.id}`} onClick={() => move(i, 1)} className="text-slate-400 hover:text-white text-xs">▼</button>
              </div>
              <GripVertical className="w-4 h-4 text-slate-500" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.text}</div>
                <div className="text-[11px] text-slate-500">
                  {p.category} · {p.icon} · {p.route}{p.featured ? ' · ⭐ Featured' : ''}
                  <span className="ml-2 text-violet-300 font-bold">{p.click_count || 0} taps</span>
                </div>
              </div>
              <Switch data-testid={`admin-prompt-toggle-${p.id}`} checked={p.status === 'active'} onCheckedChange={() => toggleStatus(p)} />
              <Button size="sm" variant="outline" onClick={() => startEdit(p)}>Edit</Button>
              <Button data-testid={`admin-prompt-delete-${p.id}`} size="sm" variant="outline" onClick={() => del(p.id)} className="text-red-300 border-red-500/30"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ============ Admin: Member Subscriptions ============
function AdminMemberSubs({ users, refresh }) {
  const [q, setQ] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [sub, setSub] = React.useState(null);
  const [plan, setPlan] = React.useState('premium');
  const [status, setStatus] = React.useState('active');
  const [adminAssigned, setAdminAssigned] = React.useState(true);
  const [reason, setReason] = React.useState('');
  const [notify, setNotify] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const filtered = (q
    ? users.filter(u =>
        u.username?.toLowerCase().includes(q.toLowerCase()) ||
        u.email?.toLowerCase().includes(q.toLowerCase()) ||
        u.id?.toLowerCase().includes(q.toLowerCase()))
    : users).slice(0, 20);

  const open = async (u) => {
    setSelected(u);
    setSub(null);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/subscription`);
      setSub(data);
      setPlan(data.current.plan === 'free' ? 'premium' : data.current.plan);
      setStatus(data.current.status === 'free' ? 'active' : data.current.status);
    } catch { toast.error('Failed to load subscription'); }
  };
  const apply = async (action) => {
    if (!selected) return;
    if (!window.confirm(`${action} @${selected.username} to ${plan.toUpperCase()}?`)) return;
    setSaving(true);
    try {
      await api.put(`/admin/users/${selected.id}/subscription`, {
        plan, status, admin_assigned: adminAssigned, reason, notify_member: notify,
      });
      toast.success('Subscription updated');
      await open(selected); refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="glass p-5 space-y-4" data-testid="admin-member-subs-panel">
      <div className="flex items-center gap-2">
        <Crown className="w-5 h-5 text-amber-400" />
        <div className="font-display font-bold">Manage Member Subscriptions</div>
      </div>
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input data-testid="admin-sub-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by username, email, or ID…" className="pl-9" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
          {filtered.map(u => (
            <button key={u.id} data-testid={`admin-sub-user-${u.id}`} onClick={() => open(u)}
                    className={`w-full text-left p-3 rounded-lg border text-sm transition-colors
                      ${selected?.id === u.id ? 'bg-violet-500/15 border-violet-500/40' : 'bg-white/5 border-white/10 hover:border-white/20'}`}>
              <div className="font-semibold truncate">{u.display_name} <span className="text-slate-400 text-xs">@{u.username}</span></div>
              <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
              <div className="text-[11px] mt-1">
                <span className={`px-1.5 py-0.5 rounded ${u.is_premium ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-700/50 text-slate-300'}`}>
                  {u.premium_tier || (u.is_premium ? 'premium' : 'free')}
                </span>
              </div>
            </button>
          ))}
          {filtered.length === 0 && <div className="text-xs text-slate-500 text-center p-4">No matches</div>}
        </div>
        <div data-testid="admin-sub-detail" className="space-y-3">
          {!selected && <div className="text-sm text-slate-400 text-center p-8 border border-dashed border-white/10 rounded-xl">Pick a member to view and change their subscription.</div>}
          {selected && sub && (
            <>
              <Card className="p-4 bg-violet-500/5 border-violet-500/30 space-y-1">
                <div className="font-display font-bold">@{sub.user.username}</div>
                <div className="text-xs text-slate-400">{sub.user.email}</div>
                <div className="flex items-center gap-2 pt-2 flex-wrap text-xs">
                  <span className="px-2 py-0.5 rounded gradient-brand text-white font-bold uppercase">{sub.current.plan}</span>
                  <span className="px-2 py-0.5 rounded bg-white/10">{sub.current.status}</span>
                  {sub.current.admin_assigned && <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300">Admin Assigned</span>}
                </div>
                {sub.current.last_payment && (
                  <div className="text-[11px] text-slate-400 mt-1">Last payment: ${((sub.current.last_payment.amount || 0)/100).toFixed(2)} · {sub.current.last_payment.lookup_key}</div>
                )}
              </Card>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">New plan</Label>
                  <select data-testid="admin-sub-plan" value={plan} onChange={e => setPlan(e.target.value)} className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm">
                    <option value="free">Free</option>
                    <option value="premium">Premium</option>
                    <option value="elite">Elite</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <select data-testid="admin-sub-status" value={status} onChange={e => setStatus(e.target.value)} className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm">
                    <option value="active">Active</option>
                    <option value="trial">Trial</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="flex items-center gap-2 text-xs"><Switch checked={adminAssigned} onCheckedChange={setAdminAssigned} /> Admin Override (no billing)</label>
                <label className="flex items-center gap-2 text-xs"><Switch checked={notify} onCheckedChange={setNotify} /> Notify member</label>
              </div>
              <div>
                <Label className="text-xs">Reason (for audit log)</Label>
                <Input data-testid="admin-sub-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="Customer support request" />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button data-testid="admin-sub-upgrade" onClick={() => apply('Upgrade')} disabled={saving} className="gradient-brand text-white">Apply change</Button>
                <Button data-testid="admin-sub-cancel" onClick={() => { setPlan('free'); setStatus('cancelled'); setTimeout(() => apply('Cancel'), 50); }} variant="outline" className="border-red-500/40 text-red-300">Cancel subscription</Button>
              </div>

              {sub.history?.length > 0 && (
                <div className="pt-3">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Change history</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {sub.history.map(h => (
                      <div key={h.id} className="text-[11px] text-slate-300 p-2 rounded bg-white/5 border border-white/10">
                        <div><strong>{h.previous_plan}</strong> → <strong>{h.new_plan}</strong> <span className="text-slate-500">· {h.new_status}</span></div>
                        <div className="text-slate-500">{new Date(h.created_at).toLocaleString()} · by @{h.admin_username || 'admin'}{h.admin_assigned ? ' · admin-assigned' : ''}</div>
                        {h.reason && <div className="text-slate-400 mt-0.5 italic">{h.reason}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}


// ============ Admin: Tier slider (daily cap editor) ============
function TierSlider({ k, icon, label, min, max, step, settings, update }) {
  const current = Number(settings[k] ?? min);
  const [val, setVal] = React.useState(current);
  const [dirty, setDirty] = React.useState(false);
  React.useEffect(() => { setVal(current); setDirty(false); }, [current]);
  // Preserve step precision (e.g., 0.05 opacity, 0.5 scale) — don't force integer.
  const roundToStep = (v) => {
    const s = Number(step) || 1;
    const r = Math.round(v / s) * s;
    // Avoid float drift like 0.30000000000004
    const decimals = (s.toString().split('.')[1] || '').length;
    return decimals ? Number(r.toFixed(decimals)) : r;
  };
  const save = () => {
    const rounded = roundToStep(val);
    if (rounded !== current) update({ [k]: rounded });
    setDirty(false);
  };
  return (
    <div data-testid={`tier-slider-${k}`} className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2 text-slate-300">
          <span className="opacity-70">{icon}</span>
          <span className="font-semibold">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-bold text-amber-300">{roundToStep(val)}</span>
          {dirty && (
            <Button data-testid={`save-${k}`} size="sm" onClick={save} className="h-6 px-2 text-[10px] gradient-brand text-white">
              Save
            </Button>
          )}
        </div>
      </div>
      <Slider
        value={[val]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => { setVal(v[0]); setDirty(true); }}
        onValueCommit={() => save()}
      />
      <div className="flex justify-between text-[10px] text-slate-500">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}



// ============ Admin: Subscription features editor (list of benefits) ============
function AdminSubFeatures({ k, label, settings, update }) {
  const [val, setVal] = React.useState((settings[k] || []).join('\n'));
  React.useEffect(() => { setVal((settings[k] || []).join('\n')); }, [settings, k]);
  const save = () => {
    const list = val.split('\n').map(s => s.trim()).filter(Boolean);
    update({ [k]: list });
  };
  return (
    <div className="mb-4" data-testid={`admin-${k}`}>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs font-bold">{label} benefits</Label>
        <Button data-testid={`save-${k}`} size="sm" variant="outline" onClick={save}>Save</Button>
      </div>
      <Textarea value={val} onChange={e => setVal(e.target.value)} rows={6}
        placeholder="One benefit per line…" className="text-sm" />
      <div className="text-[10px] text-slate-500 mt-1">{val.split('\n').filter(s => s.trim()).length} benefits · one per line</div>
    </div>
  );
}

// ============ Admin: Credits management ============
function AdminCredits({ users }) {
  const [tab, setTab] = React.useState('packages');
  const [packages, setPackages] = React.useState([]);
  const [costs, setCosts] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [userRow, setUserRow] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [newPkg, setNewPkg] = React.useState({ name: '', price_usd: 10, credits: 20, est_videos: 4, featured: false, display_order: 99, active: true });
  const [newCost, setNewCost] = React.useState({ type: '', label: '', cost_credits: 5, active: true });

  const loadAll = async () => {
    const [p, c] = await Promise.all([
      api.get('/admin/credit-packages'),
      api.get('/admin/credit-costs'),
    ]);
    setPackages(p.data); setCosts(c.data);
  };
  React.useEffect(() => { loadAll(); }, []);

  const savePkg = async (pkg) => {
    try { await api.put(`/admin/credit-packages/${pkg.id}`, pkg); toast.success('Saved'); loadAll(); }
    catch { toast.error('Save failed'); }
  };
  const addPkg = async () => {
    if (!newPkg.name) return toast.error('Name required');
    try { await api.post('/admin/credit-packages', newPkg); toast.success('Added'); setNewPkg({ ...newPkg, name: '' }); loadAll(); }
    catch { toast.error('Failed'); }
  };
  const delPkg = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    await api.delete(`/admin/credit-packages/${id}`); loadAll();
  };

  const saveCost = async (c) => {
    try { await api.put(`/admin/credit-costs/${c.id}`, c); toast.success('Saved'); loadAll(); }
    catch { toast.error('Save failed'); }
  };
  const addCost = async () => {
    if (!newCost.type || !newCost.label) return toast.error('type & label required');
    try { await api.post('/admin/credit-costs', newCost); toast.success('Added'); setNewCost({ ...newCost, type: '', label: '' }); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const delCost = async (id) => {
    if (!window.confirm('Delete this video cost?')) return;
    await api.delete(`/admin/credit-costs/${id}`); loadAll();
  };

  const loadHistory = async (u) => {
    setUserRow(u);
    const { data } = await api.get(`/admin/users/${u.id}/credits/history`);
    setHistory(data.transactions || []);
  };
  const adjust = async (amount) => {
    if (!userRow || !amount) return;
    try {
      const { data } = await api.post(`/admin/users/${userRow.id}/credits`, { amount, reason: 'admin_adjustment' });
      toast.success(`Balance: ${data.balance}`);
      loadHistory(userRow);
    } catch (e) { toast.error('Failed'); }
  };

  const filtered = users.filter(u => !search || u.username?.includes(search.toLowerCase()) || u.display_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <Card className="glass p-4 space-y-4" data-testid="admin-credits-panel">
      <div className="inline-flex p-1 rounded-full bg-white/5 border border-white/10 w-fit">
        {['packages','costs','users'].map(t => (
          <button key={t} data-testid={`admin-credits-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${tab===t?'gradient-brand text-white':'text-slate-400'}`}>
            {t === 'packages' ? 'Packages' : t === 'costs' ? 'Video Costs' : 'User Credits'}
          </button>
        ))}
      </div>

      {tab === 'packages' && (
        <div className="space-y-3">
          {packages.map(p => (
            <div key={p.id} className="rounded-xl bg-white/5 border border-white/10 p-3 grid grid-cols-2 md:grid-cols-7 gap-2 items-end" data-testid={`admin-pkg-${p.id}`}>
              <div><Label className="text-[10px]">Name</Label><Input defaultValue={p.name} onBlur={e => p.name !== e.target.value && savePkg({ ...p, name: e.target.value })} /></div>
              <div><Label className="text-[10px]">Price (USD)</Label><Input type="number" step="0.01" defaultValue={p.price_usd} onBlur={e => savePkg({ ...p, price_usd: +e.target.value })} /></div>
              <div><Label className="text-[10px]">Credits</Label><Input type="number" defaultValue={p.credits} onBlur={e => savePkg({ ...p, credits: +e.target.value })} /></div>
              <div><Label className="text-[10px]">Est. videos</Label><Input type="number" defaultValue={p.est_videos || 0} onBlur={e => savePkg({ ...p, est_videos: +e.target.value })} /></div>
              <div><Label className="text-[10px]">Order</Label><Input type="number" defaultValue={p.display_order || 0} onBlur={e => savePkg({ ...p, display_order: +e.target.value })} /></div>
              <div className="flex items-center gap-2"><Switch checked={p.active} onCheckedChange={v => savePkg({ ...p, active: v })} /><span className="text-xs">Active</span></div>
              <div className="flex items-center gap-2"><Switch checked={p.featured} onCheckedChange={v => savePkg({ ...p, featured: v })} /><span className="text-xs">Featured</span>
                <Button size="sm" variant="outline" onClick={() => delPkg(p.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
          <div className="rounded-xl bg-violet-500/5 border border-violet-500/20 p-3 grid grid-cols-2 md:grid-cols-7 gap-2 items-end" data-testid="admin-pkg-new">
            <div><Label className="text-[10px]">Name</Label><Input value={newPkg.name} onChange={e => setNewPkg({ ...newPkg, name: e.target.value })} /></div>
            <div><Label className="text-[10px]">Price</Label><Input type="number" step="0.01" value={newPkg.price_usd} onChange={e => setNewPkg({ ...newPkg, price_usd: +e.target.value })} /></div>
            <div><Label className="text-[10px]">Credits</Label><Input type="number" value={newPkg.credits} onChange={e => setNewPkg({ ...newPkg, credits: +e.target.value })} /></div>
            <div><Label className="text-[10px]">Est. videos</Label><Input type="number" value={newPkg.est_videos} onChange={e => setNewPkg({ ...newPkg, est_videos: +e.target.value })} /></div>
            <div><Label className="text-[10px]">Order</Label><Input type="number" value={newPkg.display_order} onChange={e => setNewPkg({ ...newPkg, display_order: +e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={newPkg.featured} onCheckedChange={v => setNewPkg({ ...newPkg, featured: v })} /><span className="text-xs">Featured</span></div>
            <Button data-testid="admin-pkg-add" onClick={addPkg} className="gradient-brand text-white"><Plus className="w-3 h-3 mr-1" /> Add</Button>
          </div>
        </div>
      )}

      {tab === 'costs' && (
        <div className="space-y-3">
          {costs.map(c => (
            <div key={c.id} className="rounded-xl bg-white/5 border border-white/10 p-3 grid grid-cols-2 md:grid-cols-5 gap-2 items-end" data-testid={`admin-cost-${c.id}`}>
              <div><Label className="text-[10px]">Type key</Label><Input defaultValue={c.type} onBlur={e => c.type !== e.target.value && saveCost({ ...c, type: e.target.value })} /></div>
              <div className="md:col-span-2"><Label className="text-[10px]">Label</Label><Input defaultValue={c.label} onBlur={e => c.label !== e.target.value && saveCost({ ...c, label: e.target.value })} /></div>
              <div><Label className="text-[10px]">Cost (credits)</Label><Input type="number" defaultValue={c.cost_credits} onBlur={e => saveCost({ ...c, cost_credits: +e.target.value })} /></div>
              <div className="flex items-center gap-2"><Switch checked={c.active} onCheckedChange={v => saveCost({ ...c, active: v })} /><span className="text-xs">Active</span>
                <Button size="sm" variant="outline" onClick={() => delCost(c.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
          <div className="rounded-xl bg-violet-500/5 border border-violet-500/20 p-3 grid grid-cols-2 md:grid-cols-5 gap-2 items-end" data-testid="admin-cost-new">
            <div><Label className="text-[10px]">Type key</Label><Input value={newCost.type} onChange={e => setNewCost({ ...newCost, type: e.target.value })} placeholder="e.g. premium_hd" /></div>
            <div className="md:col-span-2"><Label className="text-[10px]">Label</Label><Input value={newCost.label} onChange={e => setNewCost({ ...newCost, label: e.target.value })} /></div>
            <div><Label className="text-[10px]">Cost</Label><Input type="number" value={newCost.cost_credits} onChange={e => setNewCost({ ...newCost, cost_credits: +e.target.value })} /></div>
            <Button data-testid="admin-cost-add" onClick={addCost} className="gradient-brand text-white"><Plus className="w-3 h-3 mr-1" /> Add</Button>
          </div>
        </div>
      )}

      {tab === 'users' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <SearchIcon className="w-4 h-4 text-slate-400" />
            <Input data-testid="admin-credit-user-search" placeholder="Search by username…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
            {filtered.slice(0, 50).map(u => (
              <button key={u.id} onClick={() => loadHistory(u)}
                data-testid={`admin-credit-user-${u.id}`}
                className={`text-left p-2.5 rounded-lg border ${userRow?.id === u.id ? 'bg-violet-500/15 border-violet-500/60' : 'bg-white/5 border-white/10 hover:border-white/25'}`}>
                <div className="font-semibold text-sm">{u.display_name} <span className="text-slate-500 text-xs">@{u.username}</span></div>
                <div className="text-[11px] text-slate-400">balance: <b className="text-amber-300">{u.credits_balance || 0}</b> credits · JXP {u.jxp}</div>
              </button>
            ))}
          </div>
          {userRow && (
            <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-semibold">Managing @{userRow.username}</div>
                <div className="flex gap-2">
                  {[1, 5, 20, 50].map(n => (
                    <Button key={n} data-testid={`admin-credit-grant-${n}`} size="sm" variant="outline" onClick={() => adjust(n)}>+{n}</Button>
                  ))}
                  <Button data-testid="admin-credit-custom" size="sm" variant="outline" onClick={() => { const v = prompt('Adjust by (negative to revoke):'); if (v) adjust(+v); }}>Custom…</Button>
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {history.map(h => (
                  <div key={h.id} className="text-xs flex justify-between p-2 bg-white/5 rounded">
                    <div><b className={h.delta > 0 ? 'text-emerald-300' : 'text-rose-300'}>{h.delta > 0 ? '+' : ''}{h.delta}</b> <span className="text-slate-400">{h.kind}</span></div>
                    <div className="text-slate-500">{new Date(h.created_at).toLocaleString()}</div>
                  </div>
                ))}
                {history.length === 0 && <div className="text-xs text-slate-500 text-center p-4">No transactions yet.</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
